import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, PaperPlaneRight, Heart, PencilSimple, Trash, ArrowBendDownRight, Flag } from "@phosphor-icons/react";
import { postsApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ReportModal } from "./PostCard";
import ReelCommentsDrawer from "./ReelCommentsDrawer";
import { useTranslation } from "react-i18next";

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.round(h / 24)}d`;
}

export default function CommentsDrawer(props) {
    if (props.post?.is_reel) {
        return (
            <ReelCommentsDrawer 
                reel={{ ...props.post, id: props.post.reel_id }} 
                open={props.open} 
                onClose={props.onClose} 
                onCountChange={props.onCountChange} 
            />
        );
    }
    return <CommentsDrawerInner {...props} />;
}

function CommentsDrawerInner({ post, open, onClose, onCountChange }) {
    const { t } = useTranslation();
    const [comments, setComments] = useState([]);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [replyTo, setReplyTo] = useState(null); // comment obj
    const [editing, setEditing] = useState(null); // comment obj
    const [reportTarget, setReportTarget] = useState(null);
    const { user } = useAuth();

    const load = () => {
        if (!post) return;
        postsApi.comments(post.id).then(setComments).catch(() => {});
    };

    useEffect(() => {
        if (!open || !post) return;
        setReplyTo(null); setEditing(null); setText("");
        load();
        // eslint-disable-next-line
    }, [open, post]);

    const rootComments = useMemo(() => comments.filter((c) => !c.parent_id), [comments]);
    const repliesOf = (id) => comments.filter((c) => c.parent_id === id);

    const submit = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        if (!user) return toast.error(t("commentsDrawer.toasts.signInToComment"));
        setSending(true);
        try {
            if (editing) {
                const updated = await postsApi.editComment(editing.id, text.trim());
                setComments((prev) => prev.map((c) => c.id === editing.id ? { ...c, ...updated } : c));
                setEditing(null);
            } else {
                const created = await postsApi.addComment(post.id, text.trim(), replyTo?.id || null);
                setComments((prev) => [...prev, { ...created, like_count: 0, liked: false }]);
                onCountChange?.((comments.length + 1));
                setReplyTo(null);
            }
            setText("");
        } catch (e) {
            toast.error(t("commentsDrawer.toasts.couldNotPost"));
        } finally { setSending(false); }
    };

    const toggleLike = async (c) => {
        if (!user) return toast.error(t("commentsDrawer.toasts.signInToLike"));
        // optimistic
        setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, liked: !x.liked, like_count: (x.like_count || 0) + (x.liked ? -1 : 1) } : x));
        try { await postsApi.likeComment(c.id); } catch { load(); }
    };

    const del = async (c) => {
        if (!window.confirm(t("commentsDrawer.confirmDelete"))) return;
        try {
            await postsApi.removeComment(c.id);
            const nextComments = comments.filter((x) => x.id !== c.id && x.parent_id !== c.id);
            setComments(nextComments);
            onCountChange?.(nextComments.length);
        } catch { toast.error(t("commentsDrawer.toasts.couldNotDelete")); }
    };

    const startEdit = (c) => { setEditing(c); setReplyTo(null); setText(c.text); };
    const startReply = (c) => { setReplyTo(c); setEditing(null); setText(""); };

    return (
        <AnimatePresence>
            {open && post && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-0 md:p-4"
                    style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(8px)" }}
                    onClick={onClose}
                    data-testid="comments-drawer"
                >
                    <motion.div onClick={(e) => e.stopPropagation()}
                        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
                        className="w-full md:max-w-lg md:rounded-3xl overflow-hidden max-h-[85vh] flex flex-col"
                        style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.25)" }}
                    >
                        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <div>
                                <div className="overline mb-1">{t("commentsDrawer.overline")}</div>
                                <div className="font-serif text-lg text-white leading-tight">{t("commentsDrawer.onUserPost", { handle: post.user_handle })}</div>
                            </div>
                            <button onClick={onClose} className="w-9 h-9 rounded-full glass grid place-items-center" data-testid="comments-close" aria-label="Close">
                                <X size={14} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-5 space-y-4">
                            {rootComments.map((c) => (
                                <CommentItem key={c.id} c={c} me={user} onLike={toggleLike} onReply={startReply} onEdit={startEdit} onDelete={del} onReport={setReportTarget} />
                            ))}
                            {rootComments.map((c) => repliesOf(c.id).map((r) => (
                                <div key={r.id} className="pl-6 border-l" style={{ borderColor: "rgba(212,175,55,0.2)" }}>
                                    <CommentItem c={r} me={user} onLike={toggleLike} onReply={startReply} onEdit={startEdit} onDelete={del} onReport={setReportTarget} inReplyTo={c.user_handle} />
                                </div>
                            )))}
                            {comments.length === 0 && (
                                <div className="text-center py-10 text-white/50 text-sm">{t("commentsDrawer.empty")}</div>
                            )}
                        </div>

                        {(replyTo || editing) && (
                            <div className="px-5 py-2 text-xs text-white/60 border-t flex items-center justify-between" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                                <span>
                                    {editing ? <>{t("commentsDrawer.editingComment")}</> : <>{t("commentsDrawer.replyingTo", { handle: replyTo.user_handle })}</>}
                                </span>
                                <button onClick={() => { setReplyTo(null); setEditing(null); setText(""); }} className="hover:text-white" data-testid="comment-cancel">{t("commentsDrawer.cancel")}</button>
                            </div>
                        )}

                        <form onSubmit={submit} className="p-4 border-t flex gap-2" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <input value={text} onChange={(e) => setText(e.target.value)} placeholder={editing ? t("commentsDrawer.inputPlaceholders.edit") : replyTo ? t("commentsDrawer.inputPlaceholders.reply", { handle: replyTo.user_handle }) : t("commentsDrawer.inputPlaceholders.add")}
                                className="flex-1 bg-black/30 border rounded-full px-4 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]"
                                style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                data-testid="comment-input"
                            />
                            <button type="submit" disabled={sending || !user} className="btn-gold text-sm !py-2.5 !px-4" data-testid="comment-submit" aria-label="Send">
                                <PaperPlaneRight size={14} weight="fill" />
                            </button>
                        </form>
                    </motion.div>

                    <ReportModal open={!!reportTarget} onClose={() => setReportTarget(null)} targetType="comment" targetId={reportTarget?.id} />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function CommentItem({ c, me, onLike, onReply, onEdit, onDelete, onReport, inReplyTo }) {
    const { t } = useTranslation();
    const isMine = me && c.user_id === me.id;
    return (
        <div className="flex gap-3" data-testid={`comment-${c.id}`}>
            <img src={c.user_avatar || `https://api.dicebear.com/7.x/notionists/svg?seed=${c.user_handle}`} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
            <div className="flex-1 min-w-0">
                <div className="text-xs flex items-center gap-2 flex-wrap">
                    <span className="font-medium" style={{ color: "var(--gold)" }}>@{c.user_handle}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/40">{timeAgo(c.created_at)}</span>
                    {c.edited_at && <span className="text-white/40 italic">{t("commentsDrawer.edited")}</span>}
                    {inReplyTo && <span className="text-white/40">→ @{inReplyTo}</span>}
                </div>
                <div className="text-sm text-white/85 mt-1 leading-relaxed whitespace-pre-wrap break-words">{c.text}</div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/50">
                    <button onClick={() => onLike(c)} className="flex items-center gap-1 hover:text-[color:var(--gold)]" data-testid={`comment-like-${c.id}`}>
                        <Heart size={12} weight={c.liked ? "fill" : "regular"} color={c.liked ? "#ff5f5f" : "currentColor"} />
                        {c.like_count || 0}
                    </button>
                    <button onClick={() => onReply(c)} className="flex items-center gap-1 hover:text-white" data-testid={`comment-reply-${c.id}`}>
                        <ArrowBendDownRight size={12} /> {t("commentsDrawer.actions.reply")}
                    </button>
                    {isMine && (
                        <>
                            <button onClick={() => onEdit(c)} className="flex items-center gap-1 hover:text-white" data-testid={`comment-edit-${c.id}`}>
                                <PencilSimple size={12} /> {t("commentsDrawer.actions.edit")}
                            </button>
                            <button onClick={() => onDelete(c)} className="flex items-center gap-1 hover:text-red-300" data-testid={`comment-delete-${c.id}`}>
                                <Trash size={12} /> {t("commentsDrawer.actions.delete")}
                            </button>
                        </>
                    )}
                    {!isMine && (
                        <button onClick={() => onReport(c)} className="flex items-center gap-1 hover:text-white" data-testid={`comment-report-${c.id}`}>
                            <Flag size={12} /> {t("commentsDrawer.actions.report")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
